import React, { useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import MlxButton from '../../components/common/MlxButton/MlxButton';
import './forgotPassword.scss';
import { usePublicBrandingLogo } from '../../hooks/usePublicBrandingLogo';
import SoltBrandingLogo from '../../components/common/SoltBrandingLogo/SoltBrandingLogo';
import { routes } from '../../routes/allRoutes';
import { AlertVariant, APP_NAME, APP_YEAR } from '../../constants/constants';
import { Messages } from 'primereact/messages';
import { useMutation } from 'react-query';
import { forgotPasswordWithEmail } from '../../helpers/authHelper';
import {
    BACK_TO_LOGIN,
    EMAIL,
    ENTER_EMAIL_RESET_LINK,
    FOOTER_BRAND,
    FORGOT_PASSWORD,
    LOGIN,
    PASSWORD,
    REMEMBER_ME,
    SEND_LINK_TO_EMAIL,
    USERNAME,
} from '../../constants/strings';
import { useTranslation } from '../../hooks/useTranslation';
import HttpServiceHelper from '../../helpers/httpServiceHelper';
import { _ToastMessageProps } from '../../constants/staticTypes';
import { defaultToastData } from '../../helpers/commonHelper';
import MlxToast from '../../components/common/MlxToast/MlxToast';

const languageTexts = {
    REMEMBER_ME: REMEMBER_ME,
    USERNAME: USERNAME,
    PASSWORD: PASSWORD,
    FORGOT_PASSWORD: FORGOT_PASSWORD,
    FOOTER_BRAND: FOOTER_BRAND,
    LOGIN: LOGIN,
    SEND_LINK_TO_EMAIL: SEND_LINK_TO_EMAIL,
    BACK_TO_LOGIN: BACK_TO_LOGIN,
    EMAIL: EMAIL,
    ENTER_EMAIL_RESET_LINK: ENTER_EMAIL_RESET_LINK,
};

const ForgotPassword = () => {
    const { logo, isLoading: isLogoLoading } = usePublicBrandingLogo();
    const messageRef = useRef<Messages>(null);
    const translatedText = useTranslation(languageTexts, 'capitalize');
    const forgotPasswordMutation = useMutation(HttpServiceHelper);

    const [isLoading, setIsLoading] = useState(false);
    const [email, setEmail] = useState<string>('');
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);

        forgotPasswordWithEmail(
            forgotPasswordMutation,
            { email: email },
            (data) => {
                setToastMessage({
                    message: data?.message ?? '',
                    variant: AlertVariant.SUCCESS,
                    show: true,
                });
                setIsLoading(false);
            },
            (error: string, variant: string) => {
                setToastMessage({
                    message: error,
                    variant,
                    show: true,
                });
                setIsLoading(false);
            }
        );
    };

    return (
        <>
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <div className="login-page-container row m-0 h-100 w-100">
                <div className="col-md-6 d-none d-md-block d-flex flex-column align-items-center justify-content-center login-page-img"></div>
                <div className="col-md-6 bg-white d-flex flex-column align-items-center justify-content-between shadow">
                    <div></div>
                    <div className="login-card">
                        <SoltBrandingLogo src={logo} alt="logo" isLoading={isLogoLoading} />
                        <div className="w-100 mt-4">
                            <div className="fw-bold fs-6">{translatedText.FORGOT_PASSWORD}</div>
                            <div className="text-muted small">{translatedText.ENTER_EMAIL_RESET_LINK}</div>
                        </div>
                        <form className="w-100 login-form d-flex flex-column align-items-center" onSubmit={handleSubmit}>
                            <InputText
                                className="mt-3 w-100 p-inputtext-sm"
                                value={email}
                                type="email"
                                onChange={(e) => setEmail(e.target.value?.trim()?.toLowerCase())}
                                autoComplete="off"
                                placeholder={translatedText.EMAIL}
                            />

                            <div className="w-100 d-flex align-items-center">
                                <Messages className="w-100" ref={messageRef} pt={{ wrapper: { style: { padding: '0.5em 1rem' } } }} />
                            </div>

                            <MlxButton
                                type="submit"
                                label={translatedText.SEND_LINK_TO_EMAIL}
                                className="mt-3 w-100"
                                color="primary"
                                raised
                                loading={forgotPasswordMutation.isLoading || isLoading}
                            />

                            <small className="w-100 mt-2 d-flex flex-row justify-content">
                                <a className="text-secondary text-decoration-none" href={routes.login}>
                                    <i className="ri-arrow-left-line"></i>
                                    {translatedText.BACK_TO_LOGIN}
                                </a>
                            </small>
                        </form>
                    </div>

                    <small className="mb-2 d-flex flex-row justify-content-center align-items-center">
                        <i className="ri-copyright-line"></i>
                        <span className="ms-1">
                            {APP_YEAR} {APP_NAME}, {translatedText.FOOTER_BRAND}
                        </span>
                    </small>
                </div>
            </div>
        </>
    );
};

export default ForgotPassword;
